'use client';

import { useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { CustomImage } from 'components/shared/custom-image';
import v3 from 'components/landing/v3/v3.module.css';
import {
  bindOnceTextReveal,
  TEXT_REVEAL_FULL,
} from 'components/landing/v3/scrollBeat';
import { HeroCardArt, type HeroReason } from './HeroCardArt';
import styles from './Hero.module.css';

/** i18n keys under `hero.reasons.` in the whyUltrahuman namespace */
const REASONS: HeroReason[] = ['data', 'research', 'accuracy', 'personal'];

/** The section on this page each card leads to */
const TARGETS: Record<HeroReason, string> = {
  data: 'your-data',
  research: 'research',
  accuracy: 'clinical-accuracy',
  personal: 'powerplugs',
};

export const Hero = () => {
  const t = useTranslations('whyUltrahuman');
  const sectionRef = useRef<HTMLElement>(null);
  const cardRefs = useRef<Array<HTMLLIElement | null>>([]);
  const gsapCtx = useGsapContext(sectionRef);

  /* Each card fades up as it scrolls into view, the right-hand one of a row a
     beat after the left. */
  useIsomorphicLayoutEffect(() => {
    gsapCtx.add(() => {
      const twoUp = window.matchMedia('(min-width: 992px)').matches;
      const cleanups = cardRefs.current.map((card, i) =>
        card
          ? bindOnceTextReveal(card, {
              ...TEXT_REVEAL_FULL,
              delay: twoUp ? (i % 2) * 0.12 : 0,
            })
          : () => undefined,
      );
      return () => cleanups.forEach((cleanup) => cleanup());
    });
  }, [gsapCtx]);

  return (
    <section
      ref={sectionRef}
      className={styles.section}
      aria-labelledby="why-ultrahuman-hero-title"
    >
      <div className={styles.intro}>
        <div className={styles.heading}>
          <p className={`${v3.eyebrow} ${styles.eyebrow}`}>
            {t('hero.eyebrow')}
          </p>
          <h1
            id="why-ultrahuman-hero-title"
            className={`${v3.heroTitle} ${styles.title}`}
          >
            {t('hero.title')}
          </h1>
        </div>

        <div className={styles.stage}>
          <div className={styles.photo}>
            <CustomImage
              src="web_v2/why-ultrahuman/hero-satin-v3.jpg"
              alt={t('hero.imageAlt')}
              fill
              loading="eager"
              fetchPriority="high"
              sizes="(min-width: 992px) 100vw, 170vh"
            />
          </div>
        </div>
      </div>

      <ul className={styles.reasons}>
        {REASONS.map((key, i) => (
          <li
            key={key}
            ref={(node) => {
              cardRefs.current[i] = node;
            }}
            className={styles.card}
          >
            <HeroCardArt reason={key} />
            <div className={styles.copy}>
              <h2 className={styles.cardTitle}>
                {t(`hero.reasons.${key}.title`)}
              </h2>
              <p className={styles.cardBody}>{t(`hero.reasons.${key}.body`)}</p>
            </div>
            {/* The whole card leads to its section. */}
            <a
              href={`#${TARGETS[key]}`}
              className={styles.cardLink}
              aria-label={t('hero.learnMoreAbout', {
                topic: t(`hero.reasons.${key}.title`),
              })}
              data-buttontype={`why-uh hero ${key}`}
              onClick={(event) => {
                const target = document.getElementById(TARGETS[key]);
                if (!target) return;
                event.preventDefault();
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                /* Focus follows the scroll, so the next Tab carries on from
                   the section instead of jumping back to the cards. The
                   section is not a control, so it gets no focus ring. */
                target.tabIndex = -1;
                target.style.outline = 'none';
                target.focus({ preventScroll: true });
              }}
            />
          </li>
        ))}
      </ul>
    </section>
  );
};
